import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserGroupIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  CalendarDaysIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const TeamAttendance = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState([]);
  const [teamStats, setTeamStats] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    department: 'all'
  });
  const [filteredTeam, setFilteredTeam] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    // Sample team attendance data
    const generateTeamData = () => {
      const departments = ['Cardiology', 'Neurology', 'Pediatrics', 'Emergency', 'Surgery'];
      const positions = ['Doctor', 'Nurse', 'Technician', 'Administrator', 'Specialist'];
      
      const data = [];
      for (let i = 1; i <= 25; i++) {
        const attendanceRate = 85 + Math.random() * 15; // 85-100%
        const totalDays = 23;
        const presentDays = Math.floor((attendanceRate / 100) * totalDays);
        const absentDays = totalDays - presentDays;
        
        const statuses = ['present', 'absent', 'late', 'leave', 'wfh'];
        const todayStatus = statuses[Math.floor(Math.random() * statuses.length)];
        
        data.push({
          id: i,
          name: `Employee ${i}`,
          email: `employee${i}@apollo.com`,
          avatar: `https://images.unsplash.com/photo-${1494790108035 + i}?w=50&h=50&fit=crop&crop=face`,
          department: departments[Math.floor(Math.random() * departments.length)],
          position: positions[Math.floor(Math.random() * positions.length)],
          employeeId: `EMP${(1000 + i).toString()}`,
          todayStatus,
          checkInTime: todayStatus === 'present' || todayStatus === 'late' ? 
            `0${8 + Math.floor(Math.random() * 2)}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')} AM` : null,
          checkOutTime: todayStatus === 'present' ? 
            `0${5 + Math.floor(Math.random() * 3)}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')} PM` : null,
          todayHours: todayStatus === 'present' ? Math.round((7.5 + Math.random() * 2) * 10) / 10 : 0,
          attendanceRate: Math.round(attendanceRate * 10) / 10,
          presentDays,
          absentDays,
          lateCount: Math.floor(Math.random() * 3),
          leaveBalance: Math.floor(Math.random() * 15) + 5,
          lastActivity: todayStatus === 'present' ? 'Active now' : 
                       todayStatus === 'wfh' ? 'Working from home' :
                       todayStatus === 'leave' ? 'On leave' :
                       todayStatus === 'late' ? 'Late arrival' : 'Absent',
          workLocation: todayStatus === 'wfh' ? 'Home' : 
                       todayStatus === 'present' || todayStatus === 'late' ? 'Office - Tower A' : '--',
          monthlyHours: Math.round((170 + Math.random() * 20) * 10) / 10
        });
      }
      
      return data;
    };

    const data = generateTeamData();
    
    // Calculate team statistics
    const stats = {
      totalEmployees: data.length,
      presentToday: data.filter(emp => emp.todayStatus === 'present').length,
      absentToday: data.filter(emp => emp.todayStatus === 'absent').length,
      onLeave: data.filter(emp => emp.todayStatus === 'leave').length,
      workingFromHome: data.filter(emp => emp.todayStatus === 'wfh').length,
      lateArrivals: data.filter(emp => emp.todayStatus === 'late').length,
      averageAttendance: Math.round((data.reduce((sum, emp) => sum + emp.attendanceRate, 0) / data.length) * 10) / 10,
      totalHours: Math.round(data.reduce((sum, emp) => sum + emp.monthlyHours, 0)),
      departments: [...new Set(data.map(emp => emp.department))]
    };

    setTimeout(() => {
      setTeamData(data);
      setTeamStats(stats);
      setFilteredTeam(data);
      setLoading(false);
    }, 1000);
  }, []);

  const applyFilters = useCallback(() => {
    let filtered = [...teamData];

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(emp => 
        emp.name.toLowerCase().includes(searchTerm) ||
        emp.email.toLowerCase().includes(searchTerm) ||
        emp.employeeId.toLowerCase().includes(searchTerm) ||
        emp.department.toLowerCase().includes(searchTerm) ||
        emp.position.toLowerCase().includes(searchTerm)
      );
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(emp => emp.todayStatus === filters.status);
    }

    // Department filter
    if (filters.department !== 'all') {
      filtered = filtered.filter(emp => emp.department === filters.department);
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'attendance':
          aValue = a.attendanceRate;
          bValue = b.attendanceRate;
          break;
        case 'department':
          aValue = a.department;
          bValue = b.department;
          break;
        case 'hours':
          aValue = a.monthlyHours;
          bValue = b.monthlyHours;
          break;
        default:
          aValue = a.name;
          bValue = b.name;
      }

      if (typeof aValue === 'string') {
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });

    setFilteredTeam(filtered);
  }, [filters, teamData, sortBy, sortOrder]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'absent':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'late':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'leave':
        return <CalendarDaysIcon className="w-5 h-5 text-blue-500" />;
      case 'wfh':
        return <ClockIcon className="w-5 h-5 text-purple-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'absent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'late':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'leave':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'wfh':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-apollo-gray">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading team attendance...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Page Header */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-6 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-3 sm:mb-4">
              <UserGroupIcon className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 opacity-80" />
            </div>
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2 sm:mb-4">Team Attendance Overview</h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl opacity-90 max-w-2xl mx-auto px-4">
              Monitor your team's attendance, track performance, and manage workforce efficiently
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation */}
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 lg:gap-4 justify-center lg:justify-start">
          <button
            onClick={() => navigate('/attendance')}
            className="bg-white border border-gray-300 text-gray-700 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors font-medium text-xs sm:text-sm lg:text-base"
          >
            ← Back to Dashboard
          </button>
          <button
            onClick={() => navigate('/attendance/monthly')}
            className="bg-white border border-gray-300 text-gray-700 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-50 transition-colors font-medium text-xs sm:text-sm lg:text-base"
          >
            Monthly View
          </button>
        </div>

        {/* Team Statistics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">Team Size</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-apollo-primary truncate">{teamStats.totalEmployees}</p>
                <p className="text-xs text-gray-500 truncate">total employees</p>
              </div>
              <div className="p-2 sm:p-3 bg-blue-100 rounded-full flex-shrink-0">
                <UserGroupIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">Present Today</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600 truncate">{teamStats.presentToday}</p>
                <p className="text-xs text-gray-500 truncate">including {teamStats.workingFromHome} WFH</p>
              </div>
              <div className="p-2 sm:p-3 bg-green-100 rounded-full flex-shrink-0">
                <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">Absent Today</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-red-600 truncate">{teamStats.absentToday}</p>
                <p className="text-xs text-gray-500 truncate">{teamStats.onLeave} on planned leave</p>
              </div>
              <div className="p-2 sm:p-3 bg-red-100 rounded-full flex-shrink-0">
                <XCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4 lg:p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">Avg Attendance</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-apollo-primary truncate">{teamStats.averageAttendance}%</p>
                <p className="text-xs text-gray-500 truncate">{teamStats.lateArrivals} late arrivals</p>
              </div>
              <div className="p-2 sm:p-3 bg-purple-100 rounded-full flex-shrink-0">
                <ChartBarIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full lg:flex-1 lg:max-w-lg">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, ID, department..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 text-sm sm:text-base"
              />
            </div>

            <div className="flex w-full lg:w-auto gap-2 sm:gap-3">
              {/* Status Filter */}
              <div className="relative flex-1 lg:flex-none">
                <FunnelIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full pl-7 sm:pl-10 pr-2 sm:pr-8 py-2 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 bg-white text-xs sm:text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="late">Late</option>
                  <option value="leave">On Leave</option>
                  <option value="wfh">Work from Home</option>
                </select>
              </div>

              {/* Department Filter */}
              <select
                value={filters.department}
                onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                className="flex-1 lg:flex-none px-2 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-apollo-accent focus:border-transparent outline-none transition-all duration-200 bg-white text-xs sm:text-sm"
              >
                <option value="all">All Departments</option>
                {teamStats.departments?.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-4 sm:mb-6">
          <p className="text-sm sm:text-base text-gray-600">
            Showing {filteredTeam.length} of {teamStats.totalEmployees} team members
            {filters.search && (
              <span> matching "{filters.search}"</span>
            )}
          </p>
        </div>

        {/* Team Table */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
          {/* Mobile Card View */}
          <div className="block lg:hidden">
            <div className="divide-y divide-gray-200">
              {filteredTeam.map((employee) => (
                <div key={employee.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start space-x-3 mb-3">
                    <img
                      src={employee.avatar}
                      alt={employee.name}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">{employee.name}</div>
                      <div className="text-xs text-gray-500 truncate">{employee.employeeId}</div>
                      <div className="text-xs text-gray-400 truncate">{employee.position}</div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {getStatusIcon(employee.todayStatus)}
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(employee.todayStatus)}`}>
                        {employee.todayStatus === 'wfh' ? 'WFH' : employee.todayStatus}
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                    <div>
                      <span className="text-gray-500">Department:</span>
                      <div className="font-medium text-gray-900 truncate">{employee.department}</div>
                    </div>
                    
                    <div>
                      <span className="text-gray-500">Attendance:</span>
                      <div className="font-medium text-gray-900">{employee.attendanceRate}%</div>
                    </div>
                    
                    <div>
                      <span className="text-gray-500">Monthly Hours:</span>
                      <div className="font-medium text-gray-900">{employee.monthlyHours}h</div>
                    </div>
                    
                    <div>
                      <span className="text-gray-500">Leave Balance:</span>
                      <div className="font-medium text-gray-900">{employee.leaveBalance} days</div>
                    </div>
                  </div>
                  
                  {employee.checkInTime && (
                    <div className="text-sm bg-gray-50 rounded-lg p-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-gray-500">Check In:</span>
                          <div className="font-medium">{employee.checkInTime}</div>
                        </div>
                        {employee.checkOutTime && (
                          <div>
                            <span className="text-gray-500">Check Out:</span>
                            <div className="font-medium">{employee.checkOutTime}</div>
                          </div>
                        )}
                      </div>
                      {employee.todayHours > 0 && (
                        <div className="mt-1">
                          <span className="text-gray-500">Today's Hours:</span>
                          <span className="font-medium ml-1">{employee.todayHours}h</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="mt-3 text-xs text-gray-500">
                    Status: {employee.lastActivity}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 lg:px-6 py-3 text-left">
                    <button
                      onClick={() => handleSort('name')}
                      className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                    >
                      <span>Employee</span>
                      {sortBy === 'name' && (
                        sortOrder === 'asc' ? <ArrowUpIcon className="w-4 h-4" /> : <ArrowDownIcon className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left">
                    <button
                      onClick={() => handleSort('department')}
                      className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                    >
                      <span>Department</span>
                      {sortBy === 'department' && (
                        sortOrder === 'asc' ? <ArrowUpIcon className="w-4 h-4" /> : <ArrowDownIcon className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Today's Status
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Check In/Out
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left">
                    <button
                      onClick={() => handleSort('attendance')}
                      className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                    >
                      <span>Attendance Rate</span>
                      {sortBy === 'attendance' && (
                        sortOrder === 'asc' ? <ArrowUpIcon className="w-4 h-4" /> : <ArrowDownIcon className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left">
                    <button
                      onClick={() => handleSort('hours')}
                      className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                    >
                      <span>Monthly Hours</span>
                      {sortBy === 'hours' && (
                        sortOrder === 'asc' ? <ArrowUpIcon className="w-4 h-4" /> : <ArrowDownIcon className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTeam.map((employee) => (
                  <tr key={employee.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <img
                          src={employee.avatar}
                          alt={employee.name}
                          className="w-8 h-8 lg:w-10 lg:h-10 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{employee.name}</div>
                          <div className="text-sm text-gray-500 truncate">{employee.employeeId}</div>
                          <div className="text-xs text-gray-400 truncate">{employee.position}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{employee.department}</span>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(employee.todayStatus)}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(employee.todayStatus)}`}>
                          {employee.todayStatus === 'wfh' ? 'WFH' : employee.todayStatus}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 truncate">{employee.lastActivity}</div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      {employee.checkInTime ? (
                        <div className="text-sm">
                          <div className="text-gray-900">In: {employee.checkInTime}</div>
                          {employee.checkOutTime && (
                            <div className="text-gray-500">Out: {employee.checkOutTime}</div>
                          )}
                          {employee.todayHours > 0 && (
                            <div className="text-xs text-gray-400">{employee.todayHours}h today</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">--</span>
                      )}
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div className="text-sm font-medium text-gray-900">{employee.attendanceRate}%</div>
                        <div className="w-12 lg:w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${
                              employee.attendanceRate >= 95 ? 'bg-green-500' :
                              employee.attendanceRate >= 90 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${employee.attendanceRate}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        {employee.presentDays}P • {employee.absentDays}A • {employee.lateCount}L
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{employee.monthlyHours}h</div>
                      <div className="text-xs text-gray-500">
                        {employee.leaveBalance} days leave left
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                      <button className="text-apollo-accent hover:text-apollo-primary transition-colors">
                        <EyeIcon className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredTeam.length === 0 && (
          <div className="text-center py-12">
            <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No team members found matching your criteria</p>
            <p className="text-gray-400">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default TeamAttendance;
